import React, { ReactNode, useEffect } from 'react';
import { Form, Button, Table } from 'react-bootstrap';
import { Link, useForm, usePage } from '@inertiajs/react';

import AuthedLayout from '../../Layouts/Authed';
import Account from '../../Data/Account';
import Valuation from '../../Data/Valuation';
import AccountRow from './Accounts/AccountRow';
import { getEventValue, replaceNullWithEmpty } from './Helpers/RepeatingForms';
import FormHeader from '../../Components/Forms/FormHeader';
import InstructionsPanel from '../../Components/Forms/InstructionsPanel';
import AutosaveStatus from '../../Components/Forms/AutosaveStatus';
import MarkComplete from '../../Components/Forms/MarkComplete';
import useValuationStepAutosave from './Helpers/useValuationStepAutosave';

interface Props {
	valuation: Valuation;
	accounts: Account[];
	maxAccountsPerValuation: number;
}

interface FormData {
	accounts: Partial<Account>[];
	mark_complete: boolean;
}

const Accounts = (props: Props) => {
	const { errors, readOnly } = usePage<any>().props;

	const { data, setData, post, processing } = useForm<FormData>({
		accounts: replaceNullWithEmpty(props.accounts) ?? [],
		mark_complete: props.valuation.completed_steps?.includes('accounts') ?? false,
	});
	const autosaveStatus = useValuationStepAutosave('accounts', props.valuation.id, data);

	const submit = (event: any) => {
		event.preventDefault();
		post(`/valuation-form/${props.valuation.id}/accounts`);
	};

	const updateIndex = (event: React.ChangeEvent<any>, index: number) => {
		const newData: any = { ...data };
		newData.accounts[index][event.target.name] = getEventValue(event);
		setData(newData);
	};

	useEffect(() => {
		scrollTo(0,0);
	}, []);

	return (
		<div>
			<FormHeader
				agencyName={props.valuation.agency_name}
				effectiveDate={props.valuation.effective_date}
				pageName='Top Ten Accounts'
			/>
			<InstructionsPanel>
				Use this tab to record the agency's 10 largest accounts. If the account is personal lines use the word 'Personal' for the industry. Fill out as applicable. If you lack commission data, estimate each account's commission using your average agency commission.
			</InstructionsPanel>
			<Form onSubmit={submit} className='valuationForm'>
				<fieldset disabled={readOnly}>
				<Table striped borderless hover responsive>
					<thead>
						<tr>
							<th id='accountNum' className='cell-xs sticky-cell'>
								#
							</th>
							<th id='industry'>Industry</th>
							<th id='total_annual_premium'>
								Total Annual Premium $'s
							</th>
							<th id='total_annual_agency_commission'>
								Total Annual Agency Commission $'s
							</th>
							<th id='num_years_with_agency'>
								# Years With Agency
							</th>
							<th id='agency_producer'>Agency Producer</th>
							<th id='carrier'>Carrier</th>
						</tr>
					</thead>
					<tbody>
						{data.accounts.map(
							(account: Partial<Account>, index: number) => (
								<AccountRow
									key={`account-${index}`}
									account={account}
									index={index}
									errors={errors}
									updateIndex={updateIndex}
								/>
							)
						)}
					</tbody>
				</Table>
				</fieldset>

				<div className='d-flex justify-content-between align-items-center'>
					<Button
						as={Link as any}
						href={`/valuation-form/${props.valuation.id}/employees`}
						variant='outline-primary'
						className='mt-4'
					>
						Back
					</Button>
					<div className='d-flex align-items-center'>
						{!readOnly && <AutosaveStatus status={autosaveStatus} />}
						{!readOnly && (
							<MarkComplete
								checked={data.mark_complete}
								onChange={(checked) => setData('mark_complete', checked)}
								className='mt-4 ms-4'
							/>
						)}
						{readOnly ? (
							<Button
								as={Link as any}
								href={`/valuation-form/${props.valuation.id}/pc-carriers`}
								className='mt-4 ms-4'
							>
								Next
							</Button>
						) : (
							<Button type='submit' className='mt-4 ms-4'>
								Next
							</Button>
						)}
					</div>
				</div>
			</Form>
		</div>
	);
};

Accounts.layout = (page: ReactNode) => <AuthedLayout>{page}</AuthedLayout>;

export default Accounts;
